import type { Point, Segment, Token } from "../types";

const TREE_TRUNK_SIDES = 16;

/**
 * Returns only the physical part of a rendered prop that should stop sight.
 * Artwork such as foliage, flames, smoke, and shadows is deliberately ignored.
 */
export function propOcclusionSegments(token: Token, position: Point = token.position): Segment[] {
  if (token.effect !== "procedural-tree") return [];

  const canopyRadius = token.size * 0.42;
  const trunkRatio = token.tree?.species === "conifer" ? 0.16 : 0.19;
  const radius = Math.max(0.05, canopyRadius * trunkRatio);

  return Array.from({ length: TREE_TRUNK_SIDES }, (_, index) => {
    const start = index / TREE_TRUNK_SIDES * Math.PI * 2;
    const end = (index + 1) / TREE_TRUNK_SIDES * Math.PI * 2;
    return {
      id: `prop-trunk-${token.id}-${index}`,
      a: { x: position.x + Math.cos(start) * radius, y: position.y + Math.sin(start) * radius },
      b: { x: position.x + Math.cos(end) * radius, y: position.y + Math.sin(end) * radius },
    };
  });
}

/**
 * Samples the visible artwork footprint independently from its physical sight
 * blocker. A tree's center can be hidden by its own trunk while its canopy is
 * still plainly visible to a player.
 */
export function propVisibilitySamplePoints(token: Token, position: Point = token.position): Point[] {
  if (token.effect === "volume-fire") {
    const formScale = token.volumeFire?.form === "torch" ? 0.62 : token.volumeFire?.form === "brazier" ? 1.08 : 0.86;
    const radius = token.size * 0.42 * formScale;
    return [position, ...Array.from({ length: 8 }, (_, index) => {
      const angle = index / 8 * Math.PI * 2;
      return { x: position.x + Math.cos(angle) * radius, y: position.y + Math.sin(angle) * radius };
    })];
  }
  if (token.effect === "campfire" || token.effect === "volumetric-fire") {
    const flameRadius = token.size * 0.42;
    return [position, ...Array.from({ length: 8 }, (_, index) => {
      const angle = index / 8 * Math.PI * 2;
      return { x: position.x + Math.cos(angle) * flameRadius * 0.82, y: position.y + Math.sin(angle) * flameRadius * 0.82 };
    })];
  }
  if (token.effect === "fly-swarm" || token.effect === "twirl") {
    const swarmRadius = token.size * 0.42;
    return [position, ...Array.from({ length: 8 }, (_, index) => {
      const angle = index / 8 * Math.PI * 2;
      return { x: position.x + Math.cos(angle) * swarmRadius, y: position.y + Math.sin(angle) * swarmRadius * 0.68 };
    })];
  }
  if (token.effect !== "procedural-tree") return [position];
  const canopyRadius = token.size * 0.42;
  return [
    position,
    ...Array.from({ length: 12 }, (_, index) => {
      const angle = index / 12 * Math.PI * 2;
      return {
        x: position.x + Math.cos(angle) * canopyRadius * 0.78,
        y: position.y + Math.sin(angle) * canopyRadius * 0.64,
      };
    }),
  ];
}
